import { TOKEN_KEY } from "./apiService";
import apiService from "../services/apiService";
import { jwtDecode } from "jwt-decode";

const USER_KEY = "user";
const ADMIN_LOGIN_KEY = "is_admin_login";

const authService = {
  // ============================================================
  // USER LOGIN / OTP
  // ============================================================

  sendLoginOtp: (login) => {
    const fd = new FormData();
    fd.append("login", login);

    return apiService.create("auth/register-or-login", fd);
  },

 verifyLoginOtp: async (login, otp) => {
  const fd = new FormData();

  fd.append("login", login);
  fd.append("otp", otp);

  const response = await apiService.create(
    "auth/register-or-login-verify-otp",
    fd
  );

  const { access_token, user } = response.data;

  if (!access_token || !user) {
    throw new Error("Invalid OTP login response");
  }

  // Save token
  localStorage.setItem(TOKEN_KEY, access_token);

  // Save complete user details
  localStorage.setItem(USER_KEY, JSON.stringify(user));

  // Notify navbar/profile immediately
  window.dispatchEvent(new Event("auth-changed"));

  return response.data;
},

  // ============================================================
  // ADMIN LOGIN
  // ============================================================

  adminLogin: async (credentials) => {
    const fd = new FormData();

    /*
     * IMPORTANT
     * API expects:
     * login
     * password
     */

    fd.append("login", credentials?.login || credentials?.email || "");

    fd.append("password", credentials?.password || "");

    const response = await apiService.create("auth/admin-login", fd);

    const { access_token, user, message } = response.data;

    if (!access_token || !user) {
      throw new Error("Invalid admin login response");
    }

    // Save token
    localStorage.setItem(TOKEN_KEY, access_token);

    // Save complete user + role + permissions
    localStorage.setItem(USER_KEY, JSON.stringify(user));

    // Mark admin login
    localStorage.setItem(ADMIN_LOGIN_KEY, "true");

    console.log("ADMIN LOGIN SAVED:", {
      user,
      role: user?.role,
      permissions: user?.permissions,
    });

    return {
      access_token,
      user,
      message,
    };
  },

  // ============================================================
  // NORMAL LOGIN
  // ============================================================

  login: async (credentials) => {
    const response = await apiService.create("auth/login", credentials);

    const { access_token, user } = response.data;

    if (!access_token || !user) {
      throw new Error("Invalid login response");
    }

    localStorage.setItem(TOKEN_KEY, access_token);

    localStorage.setItem(USER_KEY, JSON.stringify(user));

    return response.data;
  },

  // ============================================================
  // TOKEN
  // ============================================================

  getToken: () => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // ============================================================
  // CURRENT USER
  // ============================================================

  getCurrentUser: () => {
    const userStr = localStorage.getItem(USER_KEY);

    if (!userStr) {
      return null;
    }

    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Invalid user data in localStorage");

      return null;
    }
  },

  // ============================================================
  // ROLE HELPERS
  // ============================================================

  getRole: () => {
    const user = authService.getCurrentUser();

    return user?.role || null;
  },

  getRoleId: () => {
    const user = authService.getCurrentUser();

    return user?.role?.id || user?.user_role_id || null;
  },

  getRoleName: () => {
    const user = authService.getCurrentUser();

    return user?.role?.name || "";
  },

  getRoleSlug: () => {
    const user = authService.getCurrentUser();

    return user?.role?.slug || "";
  },

  // ============================================================
  // PERMISSIONS
  // ============================================================

  getPermissions: () => {
    const user = authService.getCurrentUser();

    return user?.permissions || {};
  },

  getModulePermissions: (module) => {
    const permissions = authService.getPermissions();

    return permissions?.[module]?.permissions || {};
  },

  // ============================================================
  // MAIN PERMISSION CHECK
  // ============================================================

  hasPermission: (module, action = "view") => {
    const permissions = authService.getPermissions();

    const modulePermission = permissions?.[module];

    if (!modulePermission) {
      return false;
    }

    const actions = modulePermission.permissions;

    if (!actions) {
      return false;
    }

    return actions[action] === true;
  },

  // ============================================================
  // MODULE VIEW CHECK
  // ============================================================

  hasModulePermission: (module) => {
    return authService.hasPermission(module, "view");
  },

  // ============================================================
  // ROLE CHECK
  // ============================================================

  hasRole: (roleSlug) => {
    return authService.getRoleSlug() === roleSlug;
  },

  hasAnyRole: (roles = []) => {
    const currentRole = authService.getRoleSlug();

    return roles.includes(currentRole);
  },

  // ============================================================
  // ADMIN LOGIN CHECK
  // ============================================================

  isAdminLoggedIn: () => {
    const token = localStorage.getItem(TOKEN_KEY);

    const user = authService.getCurrentUser();

    const adminLogin = localStorage.getItem(ADMIN_LOGIN_KEY);

    if (!token || !user || adminLogin !== "true") {
      return false;
    }

    try {
      const decoded = jwtDecode(token);

      if (decoded?.exp) {
        return decoded.exp * 1000 > Date.now();
      }

      return true;
    } catch (error) {
      return false;
    }
  },

  // ============================================================
  // TOKEN EXPIRED
  // ============================================================

  isTokenExpired: () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      return true;
    }

    try {
      const decoded = jwtDecode(token);

      if (!decoded?.exp) {
        return false;
      }

      return decoded.exp * 1000 < Date.now();
    } catch (error) {
      return true;
    }
  },

  // ============================================================
  // GENERAL LOGIN CHECK
  // ============================================================

  isLoggedIn: () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      localStorage.removeItem(USER_KEY);

      return false;
    }

    try {
      const decoded = jwtDecode(token);

      if (!decoded?.exp) {
        return true;
      }

      const expired = decoded.exp * 1000 < Date.now();

      if (expired) {
        localStorage.removeItem(TOKEN_KEY);

        localStorage.removeItem(USER_KEY);

        localStorage.removeItem(ADMIN_LOGIN_KEY);

        return false;
      }

      return true;
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);

      localStorage.removeItem(USER_KEY);

      localStorage.removeItem(ADMIN_LOGIN_KEY);

      return false;
    }
  },

  // ============================================================
  // AUTH HEADER
  // ============================================================

  getAuthHeader: () => {
    const token = authService.getToken();

    if (!token) {
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  },

  // ============================================================
  // LOGOUT
  // ============================================================

  logout: async () => {
    try {
      const response = await apiService.create("auth/logout", {});

      if (response?.status === 200 || response?.status === 204) {
        localStorage.removeItem(TOKEN_KEY);

        localStorage.removeItem(USER_KEY);

        localStorage.removeItem(ADMIN_LOGIN_KEY);

        window.dispatchEvent(new Event("storage"));

        return true;
      }

      throw new Error("Logout API failed");
    } catch (error) {
      // Even if logout API fails,
      // clear local login
      localStorage.removeItem(TOKEN_KEY);

      localStorage.removeItem(USER_KEY);

      localStorage.removeItem(ADMIN_LOGIN_KEY);

      window.dispatchEvent(new Event("storage"));

      return true;
    }
  },

  // ============================================================
  // PASSWORD RESET
  // ============================================================

  requestPasswordResetOtp: (login) => {
    const fd = new FormData();

    fd.append("login", login);

    return apiService.create("auth/password/request-otp", fd);
  },

  resetPassword: ({ login, otp, password, password_confirmation }) => {
    const fd = new FormData();

    fd.append("login", login);

    fd.append("otp", otp);

    fd.append("password", password);

    fd.append("password_confirmation", password_confirmation);

    return apiService.create("auth/password/reset", fd);
  },

  // ============================================================
  // ADMIN REGISTER
  // ============================================================

  registerAdmin: async (data) => {
    return apiService.create("backend/auth/register-user-admin", data);
  },

  register: async (data) => {
    return apiService.create("auth/register", data);
  },

  // ============================================================
  // OTP
  // ============================================================

  resendOtp: (login) => {
    const fd = new FormData();

    fd.append("login", login);

    return apiService.create("auth/resend-otp", fd);
  },

  verifyOtp: (login, otp) => {
    const fd = new FormData();

    fd.append("login", login);

    fd.append("otp", otp);

    return apiService.create("auth/verify-otp", fd);
  },

  // ============================================================
  // PASSWORD CHANGE
  // ============================================================

  changePasswordUser: async (data) => {
    return apiService.create("backend/auth/user-change-password", data);
  },

  changePassword: async (data) => {
    return apiService.create("backend/auth/change-password", data);
  },
};

export default authService;
